<template>
    <AppLayout>
        <template #icon>
            <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M12.5278 14.9722H7.63889M22.3056 10.0833H17.4167M2.75 2.75V22.3056C2.75 22.9539 3.00754 23.5756 3.46596 24.034C3.92438 24.4925 4.54614 24.75 5.19444 24.75H24.75M18.6389 5.19444H21.0833C21.7583 5.19444 22.3056 5.74165 22.3056 6.41667V18.6389C22.3056 19.3139 21.7583 19.8611 21.0833 19.8611H18.6389C17.9639 19.8611 17.4167 19.3139 17.4167 18.6389V6.41667C17.4167 5.74165 17.9639 5.19444 18.6389 5.19444ZM8.86111 8.86111H11.3056C11.9806 8.86111 12.5278 9.40832 12.5278 10.0833V18.6389C12.5278 19.3139 11.9806 19.8611 11.3056 19.8611H8.86111C8.1861 19.8611 7.63889 19.3139 7.63889 18.6389V10.0833C7.63889 9.40832 8.1861 8.86111 8.86111 8.86111Z"
                    stroke="url(#paint0_linear_1590_256)"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <defs>
                    <linearGradient
                        id="paint0_linear_1590_256"
                        x1="3.81183"
                        y1="4.95051"
                        x2="16.9367"
                        y2="11.2426"
                        gradientUnits="userSpaceOnUse"
                    >
                        <stop stop-color="#FF8D47" />
                        <stop offset="0.147634" stop-color="#FF7447" />
                        <stop offset="0.454086" stop-color="#FF4247" />
                        <stop offset="0.636926" stop-color="#E92A5B" />
                        <stop offset="0.740367" stop-color="#DD1C67" />
                        <stop offset="0.842991" stop-color="#D31071" />
                        <stop offset="1" stop-color="#CF0283" />
                    </linearGradient>
                </defs>
            </svg>
        </template>
        <template #title> {{ $t("team_reports.title") }} </template>
        <template #description> {{ $t("team_reports.description") }} </template>

        <template #actions>
            <TeamSwitcher :teams="teams" :selected-team="selectedTeam" />
        </template>

        <div class="p-5">
            <div class="flex justify-between">
                <div class="flex items-center gap-2">
                    <p class="text-lg font-medium tracking-tight">
                        {{ selectedTeam.name }}
                    </p>

                    <div
                        class="rounded-md border border-iron-300 bg-white px-2 py-[3px] text-xs font-medium tracking-tight text-iron-700"
                    >
                        {{ teamMembers.length }}
                        {{ $t("team_reports.members", teamMembers.length) }}
                    </div>
                </div>

                <div class="flex gap-4">
                    <div>
                        <TeamMemberSwitcher
                            :team-members="teamMembers"
                            :selected-team-member="selectedUser"
                            @select-user="selectUser"
                        />
                    </div>
                    <div>
                        <EventTypeSwitcher
                            :event-types="eventTypes"
                            :team-id="selectedTeam.id"
                            :selected-event-type="selectedEventType"
                            @update:selected-event-type="selectEventType"
                        />
                    </div>
                </div>
            </div>

            <div>
                <div>
                    <div
                        class="mt-4 h-full rounded-xl border border-iron-200 bg-white px-10 py-5 shadow-sm"
                    >
                        <h2 class="text-lg font-semibold tracking-tight">
                            {{ $t("team_reports.chart_card.title") }}
                        </h2>

                        <Tabs class="mt-4" default-value="past">
                            <TabsList>
                                <TabsTrigger value="past">
                                    {{ $t("team_reports.chart_card.monthly") }}
                                </TabsTrigger>
                                <TabsTrigger value="past_by_user">
                                    {{ $t("team_reports.chart_card.by_user") }}
                                </TabsTrigger>
                                <TabsTrigger value="past_by_event">
                                    {{ $t("team_reports.chart_card.by_event") }}
                                </TabsTrigger>
                            </TabsList>
                            <TabsContent class="pt-10" value="past">
                                <TotalChart :data="pastReservationsByMonth" />
                            </TabsContent>
                            <TabsContent class="pt-10" value="past_by_user">
                                <DonutChart :data="pastReservationsByUser" />
                            </TabsContent>
                            <TabsContent class="pt-10" value="past_by_event">
                                <DonutChart
                                    :data="pastReservationsByEventType"
                                />
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
            </div>

            <div
                class="mb-12 mt-12 rounded-xl border border-iron-200 bg-white shadow-sm"
            >
                <div class="p-5">
                    <h1
                        class="mt-2 text-lg font-medium tracking-tight text-iron-700"
                    >
                        {{ $t("team_reports.reservations") }}
                    </h1>
                </div>
                <ReservationsTable
                    :data="reservations"
                    :team-members="teamMembers"
                    @prev-page="prevPage"
                    @next-page="nextPage"
                    @set-page="setPage"
                />
            </div>
        </div>
    </AppLayout>
</template>

<script setup lang="ts">
import AppLayout from "../../../../Layouts/AppLayout.vue";

import TotalChart from "./components/TotalChart.vue";
import DonutChart from "./components/DonutChart.vue";
import ReservationsTable from "./components/ReservationsTable.vue";
import TeamSwitcher from "./components/TeamSwitcher.vue";
import EventTypeSwitcher from "./components/EventTypeSwitcher.vue";
import TeamMemberSwitcher from "./components/TeamMemberSwitcher.vue";
import { User } from "../EventTypes/types/EventType";
import { EventType } from "../EventTypes/types/EventType";
import { Team } from "../Teams/types/Team";
import { PastReservation } from "./types";
import { ref } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { Reservation } from "../Dashboard/components/event.vue";
import type { PaginatedData } from "../../../../types/Pagination";
import { useUrlSearchParams } from "@vueuse/core";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChartArea } from "lucide-vue-next";

type Params = {
    userId: string;
    eventTypeIds: string;
    page: number;
};

const params = useUrlSearchParams<Params>();

params;

const props = defineProps<{
    pastReservationsByMonth: PastReservation[];
    pastReservationsByUser: PastReservation[];
    pastReservationsByEventType: PastReservation[];
    upcomingReservations: PaginatedData<Reservation>;
    canceledReservations: PaginatedData<Reservation>;
    recentlyBookedReservations: PaginatedData<Reservation>;
    teamMembers: User[];
    eventTypes: EventType[];
    teams: Team[];
    selectedTeam: Team;
    reservations: PaginatedData<Reservation>;
}>();

console.log(props);

const selectedUser = ref<User | undefined>(
    props.teamMembers.find((member) => member.id.toString() === params.userId),
);
const selectedEventType = ref<string>(params.eventTypeIds);
const currentPage = ref(1);

console.log(props.reservations);

function visitWithQueryData() {
    router.visit(
        route("app.teams.reports.index", {
            team: props.selectedTeam.id,
            _query: {
                userId: selectedUser.value?.id,
                eventTypeIds: [selectedEventType.value],
                page: currentPage.value,
            },
        }),

        {
            preserveState: true,
            only: [
                "pastReservationsByMonth",
                "pastReservationsByUser",
                "pastReservationsByEventType",
                "reservations",
                "upcomingReservations",
                "canceledReservations",
                "recentlyBookedReservations",
            ],
        },
    );
}

function selectUser(user: User | undefined) {
    if (!user) {
        selectedUser.value = undefined;
    } else if (selectedUser.value && user.id === selectedUser.value.id) {
        selectedUser.value = undefined;
    } else {
        selectedUser.value = user;
    }
    currentPage.value = 1;

    visitWithQueryData();
}

function nextPage() {
    currentPage.value++;
    visitWithQueryData();
}

function prevPage() {
    currentPage.value--;
    visitWithQueryData();
}

function setPage(page: number) {
    currentPage.value = page;
    visitWithQueryData();
}

function selectEventType(eventTypeId: string) {
    selectedEventType.value = eventTypeId;
    currentPage.value = 1;
    visitWithQueryData();
}
</script>

<style>
.gradient {
    border: 1.8px solid;

    border-image-source: linear-gradient(
        137.17deg,
        #ff8d47 10.41%,
        #ff7447 22.14%,
        #ff4247 46.48%,
        #e92a5b 61%,
        #dd1c67 69.21%,
        #d31071 77.36%,
        #cf0283 89.83%
    );
}
</style>
